import { ServiceAccountJsonUpload } from '@/components/admin/ServiceAccountJsonUpload';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import {
  type DataSource,
  type UpdateDataSourceRequest,
  UpdateDataSourceRequestSchema,
} from '@innermate/shared';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

interface BigQueryEditFormProps {
  dataSource: DataSource;
  submitting?: boolean;
  onSubmit: (body: UpdateDataSourceRequest) => void;
  onCancel?: () => void;
}

type TestStatus = 'idle' | 'testing' | 'ok' | 'fail';

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

export function BigQueryEditForm({
  dataSource,
  submitting,
  onSubmit,
  onCancel,
}: BigQueryEditFormProps) {
  const extra = dataSource.extraConfig ?? {};
  const [name, setName] = useState(dataSource.name);
  const [dataProject, setDataProject] = useState(dataSource.database);
  const [location, setLocation] = useState(str(extra.location));
  const [datasets, setDatasets] = useState(dataSource.schemaFilter.join(', '));
  const [description, setDescription] = useState(dataSource.description ?? '');
  // 재업로드 시에만 채워짐 — 빈값이면 기존 자격증명 유지.
  const [credentials, setCredentials] = useState('');
  const [billingProject, setBillingProject] = useState(str(extra.projectId));
  const [clientEmail, setClientEmail] = useState(dataSource.username);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const resetTest = () => {
    if (testStatus !== 'idle') {
      setTestStatus('idle');
      setTestMessage(null);
    }
  };

  const buildUpdate = (): UpdateDataSourceRequest | null => {
    const draft: Record<string, unknown> = {
      name,
      database: dataProject,
      schemaFilter: datasets
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
      extraConfig: {
        projectId: billingProject,
        ...(location.trim() ? { location: location.trim() } : {}),
      },
      description: description.length === 0 ? null : description,
    };
    // 자격증명을 새로 올린 경우에만 password/username 갱신. 아니면 기존 유지.
    if (credentials) {
      draft.password = credentials;
      draft.username = clientEmail;
    }
    const parsed = UpdateDataSourceRequestSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = String(issue.path[0] ?? '_');
        if (!next[k]) next[k] = issue.message;
      }
      setErrors(next);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const onTest = async () => {
    const body = buildUpdate();
    if (!body) return;
    setTestStatus('testing');
    setTestMessage(null);
    try {
      const res = await api.dataSourceTestConnectionById(dataSource.id, body);
      if (res.ok) {
        setTestStatus('ok');
        setTestMessage(`연결 성공 (${res.latencyMs}ms)`);
      } else {
        setTestStatus('fail');
        setTestMessage(res.error ?? '알 수 없는 오류');
      }
    } catch (err) {
      setTestStatus('fail');
      setTestMessage(err instanceof ApiError ? err.message : (err as Error).message);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (testStatus !== 'ok') return;
    const body = buildUpdate();
    if (!body) return;
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field label="이름" htmlFor="bqe-name" error={errors.name} hint="식별용. 공백 가능.">
        <Input
          id="bqe-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            resetTest();
          }}
        />
      </Field>

      <ServiceAccountJsonUpload
        id="bqe-file"
        label="서비스계정 키 (JSON) — 교체 시에만 업로드"
        hint="비워두면 기존 자격증명을 유지합니다. 키를 바꾸려면 새 JSON 을 업로드하세요."
        error={errors.password}
        placeholder="기존 유지 (JSON 파일 선택…)"
        onLoaded={(json, parsed) => {
          setCredentials(json);
          setBillingProject(parsed.project_id);
          if (parsed.client_email) setClientEmail(parsed.client_email);
          resetTest();
        }}
        onError={() => setCredentials('')}
        summary={
          <p className="mt-1 text-xs text-foreground-muted">
            {credentials
              ? `✓ 새 키: ${billingProject} · ${clientEmail}`
              : `현재: ${billingProject || '—'} · ${clientEmail}`}
          </p>
        }
      />

      <div className="grid grid-cols-2 gap-4">
        <Field label="데이터 프로젝트 ID" htmlFor="bqe-data-project" error={errors.database}>
          <Input
            id="bqe-data-project"
            value={dataProject}
            onChange={(e) => {
              setDataProject(e.target.value);
              resetTest();
            }}
          />
        </Field>
        <Field label="위치 (location, 선택)" htmlFor="bqe-location">
          <Input
            id="bqe-location"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              resetTest();
            }}
            placeholder="예: US"
          />
        </Field>
      </div>

      <Field
        label="데이터셋 필터 (선택)"
        htmlFor="bqe-datasets"
        error={errors.schemaFilter}
        hint="쉼표/줄바꿈 구분. 비우면 데이터 프로젝트의 모든 데이터셋."
      >
        <textarea
          id="bqe-datasets"
          value={datasets}
          onChange={(e) => {
            setDatasets(e.target.value);
            resetTest();
          }}
          rows={2}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
        />
      </Field>

      <Field label="설명 (관리자용 메모)" htmlFor="bqe-desc" error={errors.description}>
        <textarea
          id="bqe-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
        />
      </Field>

      <div className="rounded-xl border border-border bg-surface-muted/30 p-4">
        <p className="mb-2 text-xs text-foreground-muted">
          변경 후 연결 테스트를 통과해야 저장됩니다.
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onTest}
            disabled={testStatus === 'testing'}
          >
            {testStatus === 'testing' ? <Loader2 className="size-4 animate-spin" /> : null}
            연결 테스트
          </Button>
          {testStatus === 'ok' ? (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="size-3.5" />
              {testMessage}
            </span>
          ) : testStatus === 'fail' ? (
            <span className="inline-flex items-center gap-1 text-xs text-destructive">
              <XCircle className="size-3.5" />
              {testMessage}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting || testStatus !== 'ok'}>
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          변경사항 저장
        </Button>
      </div>
    </form>
  );
}
